class RoRelatedProducts extends HTMLElement {
  static VISITOR_HISTORY_TYPE = 'recommendations/visitor/history';

  static LOADED_CLASS = 'ro-related-products--loaded';

  constructor() {
    super();
    this.onConsentUpdated = this.onConsentUpdated.bind(this);
  }

  connectedCallback() {
    if (!this.dataset.ak) {
      console.error('Related products: API key is missing');
      return;
    }

    if (!this.dataset.type) {
      console.warn('Related products: no type specified');
      return;
    }

    this.fetchWhenConsentApproved();
  }

  disconnectedCallback() {
    this.clearConsentWatchers();
  }

  /**
   * Stops listening for consent updates.
   * @returns {void}
   */
  clearConsentWatchers() {
    window.removeEventListener('ro:cookie-consent-updated', this.onConsentUpdated);
    window.clearInterval(this.consentPollInterval);
  }

  /**
   * Loads Clerk recommendations after analytics consent is granted.
   * @returns {void}
   */
  fetchWhenConsentApproved() {
    if (this.relatedProductsLoaded) {
      return;
    }

    const loadRecommendations = () => {
      if (this.relatedProductsLoaded) {
        return;
      }

      this.relatedProductsLoaded = true;
      this.clearConsentWatchers();
      this.fetch();
    };

    if (!window.RoRelatedProductsConfig?.consentRequired) {
      loadRecommendations();
      return;
    }

    if (window.RoClerkConsentApproved === true) {
      loadRecommendations();
      return;
    }

    if (window.RoClerkConsentApproved === false) {
      return;
    }

    window.addEventListener('ro:cookie-consent-updated', this.onConsentUpdated);

    this.consentPollInterval = window.setInterval(() => {
      if (window.RoClerkConsentApproved === true) {
        loadRecommendations();
      }
    }, 300);
  }

  /**
   * Handles cookie consent updates from Iubenda.
   * @param {CustomEvent} event
   * @returns {void}
   */
  onConsentUpdated(event) {
    if (event?.detail?.clerkConsentApproved !== true || this.relatedProductsLoaded) {
      return;
    }

    this.relatedProductsLoaded = true;
    this.clearConsentWatchers();
    this.fetch();
  }

  /**
   * Builds the Clerk API query object for the current recommendation type.
   * @returns {object}
   */
  buildQueryObj() {
    const queryObj = {
      key: this.dataset.ak,
      limit: 4,
      attributes: ['handle'],
    };

    if (this.dataset.productId) {
      queryObj.exclude = [this.dataset.productId];
    }

    if (
      this.dataset.type !== RoRelatedProducts.VISITOR_HISTORY_TYPE &&
      this.dataset.type !== 'recommendations/popular' &&
      this.dataset.productId
    ) {
      queryObj.products = [this.dataset.productId];
    }

    if (this.dataset.productGender) {
      queryObj.filter = `gender = '${RoRelatedProducts.escapeFilterValue(this.dataset.productGender)}'`;
    }

    return queryObj;
  }

  fetch() {
    Clerk(
      'call',
      this.dataset.type,
      this.buildQueryObj(),
      (response) => {
        const handles = [
          ...new Set(
            (response.product_data ?? [])
              .map((product) => product.handle)
              .filter(Boolean)
          ),
        ];

        if (handles.length) {
          this.loadSection(handles);
        }
      },
      (error) => {
        console.error('Related products: Clerk.io error:', error);
      }
    );
  }

  /**
   * Loads the Shopify section that renders the product cards.
   * @param {string[]} handles
   * @returns {void}
   */
  loadSection(handles) {
    const url =
      `${window.location.pathname}?sections=ro-related-products-content&handles=` +
      encodeURIComponent(handles.join(','));

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const innerHtml = this.getSectionInnerHTML(data['ro-related-products-content']);

        if (!innerHtml?.trim()) {
          return;
        }

        this.innerHTML = innerHtml;
        this.markLoadedIfHasProducts();
      })
      .catch((error) => {
        console.error('Error loading the section:', error);
      });
  }

  /**
   * Marks the component as loaded when product cards are present.
   * @returns {void}
   */
  markLoadedIfHasProducts() {
    const hasProducts =
      this.querySelector('[data-clerk-product-id]') ||
      (this.querySelector('.tw-grid')?.childElementCount ?? 0) > 0;

    if (hasProducts) {
      this.classList.add(RoRelatedProducts.LOADED_CLASS);
    }
  }

  /**
   * Extracts the inner HTML of a rendered Shopify section.
   * @param {string} html
   * @param {string} [selector='.shopify-section']
   * @returns {string}
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);

    return section?.innerHTML ?? '';
  }

  /**
   * Escapes single quotes in Clerk filter values.
   * @param {string} value
   * @returns {string}
   */
  static escapeFilterValue(value) {
    return String(value).replace(/'/g, "\\'");
  }
}

customElements.define('ro-related-products', RoRelatedProducts);

class RoComplementaryProducts extends RoRelatedProducts {
  static CLERK_API = 'recommendations/complementary';
  static CLERK_FALLBACK_API = 'recommendations/trending';
  static CLERK_LIMIT = 4;

  connectedCallback() {
    if (!this.dataset.ak) {
      console.error('Styled with products: API key is missing');
      return;
    }

    this.fetch();
  }

  /**
   * Loads complementary products from Clerk in API order; falls back to trending when empty.
   * @returns {Promise<void>}
   */
  async fetch() {
    let handles = this.getHandlesFromProducts(await this.fetchComplementaryProducts());

    if (!handles.length) {
      handles = this.getHandlesFromProducts(await this.fetchTrendingProducts());
    }

    if (handles.length) {
      this.loadSection(handles);
    }
  }

  /**
   * Fetches complementary recommendations for the current product.
   * @returns {Promise<object[]>}
   */
  fetchComplementaryProducts() {
    const queryObj = this.getBaseQuery();

    if (this.dataset.productId) {
      queryObj.products = [this.dataset.productId];
    }

    return this.fetchClerkProducts(RoComplementaryProducts.CLERK_API, queryObj, 'Styled with products');
  }

  /**
   * Fetches trending recommendations used when complementary returns no products.
   * @returns {Promise<object[]>}
   */
  fetchTrendingProducts() {
    return this.fetchClerkProducts(
      RoComplementaryProducts.CLERK_FALLBACK_API,
      this.getBaseQuery(),
      'Styled with products fallback'
    );
  }

  /**
   * Calls a Clerk recommendations endpoint and resolves with product_data.
   * @param {string} api
   * @param {object} queryObj
   * @param {string} logPrefix
   * @returns {Promise<object[]>}
   */
  fetchClerkProducts(api, queryObj, logPrefix) {
    return new Promise((resolve) => {
      Clerk(
        'call',
        api,
        queryObj,
        (response) => {
          const products = response.product_data ?? [];
          resolve(products);
        },
        (response) => {
          console.error(`${logPrefix}: Clerk.io error:`, response);
          resolve([]);
        }
      );
    });
  }

  /**
   * Builds the Clerk query for complementary / trending requests.
   * @returns {object}
   */
  getBaseQuery() {
    const queryObj = {
      key: this.dataset.ak,
      limit: RoComplementaryProducts.CLERK_LIMIT,
      attributes: ['handle'],
    };

    if (this.dataset.productId) {
      queryObj.exclude = [this.dataset.productId];
    }

    return queryObj;
  }

  /**
   * Extracts unique product handles while preserving Clerk response order.
   * @param {object[]} products
   * @returns {string[]}
   */
  getHandlesFromProducts(products) {
    return [
      ...new Set(
        (products ?? [])
          .map((product) => product.handle)
          .filter(Boolean)
      ),
    ];
  }
}

customElements.define('ro-complementary-products', RoComplementaryProducts);
